import * as echarts from 'echarts';
import type { EChartsOption } from 'echarts';
import { DownloadConfig, LegendItem } from './types';
import { getFieldLabel } from './utils';
import { FONT_FAMILY_HEADING, FONT_FAMILY_CHART } from './constants';
import Alert from '../../utils/alert';

// ─── Layout constants (all in logical pixels at 1x) ──────────────────────────

const MAX_LOGO_WIDTH_PX = 200;
const LOGO_PADDING_PX = 20;
const HEADING_HEIGHT_PX = 120;
const MAIN_HEADING_FONT_SIZE_PX = 28;
const SUB_HEADING_FONT_SIZE_PX = 23;
const HEADING_LOGO_WIDTH_PX = 60;
const LOGO_COMPANY_NAME_GAP_PX = 12;
const NAME_SUBHEADING_GAP_PX = 18;
const HEADING_PADDING_TOP_PX = 10;
const SUBHEADING_LINE_HEIGHT_MULTIPLIER = 1.4;
const HEADING_TITLE_MAX_WIDTH_RATIO = 0.6;
const HEADING_SUBTITLE_MAX_WIDTH_RATIO = 0.8;
const LEGEND_TOP_MARGIN_PX = 16;
const LEGEND_BOTTOM_MARGIN_PX = 0;
const LEGEND_ROW_HEIGHT_PX = 24;
const LEGEND_INDICATOR_SIZE_PX = 10;
const LEGEND_FONT_SIZE_PX = 15;
const LEGEND_INDICATOR_TEXT_GAP_PX = 8;
const LEGEND_COLUMN_GAP_PX = 24;
const LEGEND_PADDING_X_PX = 50;
const ITEMS_PER_COLUMN = 4;
const VS_GAP_PX = 12;

export { HEADING_HEIGHT_PX };

// ─── Shared types ────────────────────────────────────────────────────────────

export type CompanyHeadingInfo = {
    companyName: string;
    companyLogoURL: string | null;
};

// ─── Dimension helpers ────────────────────────────────────────────────────────

export function calculateScaledDimensions(
    width: number,
    height: number,
    resolution: number,
): { scaledWidth: number; scaledHeight: number } {
    return { scaledWidth: width * resolution, scaledHeight: height * resolution };
}

export function calculateLegendHeight(itemCount: number, resolution: number): number {
    if (itemCount === 0) {
        return 0;
    }
    const rows = Math.min(itemCount, ITEMS_PER_COLUMN);
    return (
        rows * LEGEND_ROW_HEIGHT_PX * resolution +
        LEGEND_TOP_MARGIN_PX * resolution +
        LEGEND_BOTTOM_MARGIN_PX * resolution
    );
}

// ─── Text utilities ───────────────────────────────────────────────────────────

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
    if (!text?.trim()) {
        return [];
    }
    const words = text.split(' ');
    const lines: string[] = [];
    let current = '';

    for (const word of words) {
        const test = current ? `${current} ${word}` : word;
        if (ctx.measureText(test).width <= maxWidth) {
            current = test;
        } else {
            if (current) lines.push(current);
            current = word;
        }
    }
    if (current) lines.push(current);
    return lines;
}

function truncateText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
    if (ctx.measureText(text).width <= maxWidth) {
        return text;
    }
    let low = 0;
    let high = text.length;
    let result = text;
    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const candidate = text.substring(0, mid) + '...';
        if (ctx.measureText(candidate).width <= maxWidth) {
            result = candidate;
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }
    return result;
}

// ─── Canvas drawing helpers ───────────────────────────────────────────────────

function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
    const radius = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
}

type DrawHeadingsParams = {
    ctx: CanvasRenderingContext2D;
    config: DownloadConfig;
    companies: CompanyHeadingInfo[];
    heading: string;
    isEmployee: boolean;
    canvasWidth: number;
};

async function drawHeadings({
    ctx,
    config,
    companies,
    heading,
    isEmployee,
    canvasWidth,
}: DrawHeadingsParams): Promise<void> {
    if (!config.showHeadings) {
        return;
    }

    const font = FONT_FAMILY_HEADING;
    const res = config.resolution;
    const headingFontSize = Math.round(MAIN_HEADING_FONT_SIZE_PX * res);
    const subheadingFontSize = Math.round(SUB_HEADING_FONT_SIZE_PX * res);
    ctx.fillStyle = '#000000';

    // Pre-calculate subheading lines
    let subheadingLines: string[] = [];
    if (isEmployee && heading) {
        ctx.font = `400 ${subheadingFontSize}px ${font}`;
        subheadingLines = wrapText(ctx, heading, canvasWidth * HEADING_SUBTITLE_MAX_WIDTH_RATIO);
    }
    const subheadingLineHeight = Math.round(subheadingFontSize * SUBHEADING_LINE_HEIGHT_MULTIPLIER);
    const subheadingHeight = subheadingLines.length * subheadingLineHeight;

    // Load all company logos in parallel
    const logoImages = await Promise.all(
        companies.map(async (c) => {
            if (!c.companyLogoURL) {
                return null;
            }
            try {
                return await loadImage(c.companyLogoURL);
            } catch (err) {
                Alert.warn(`Failed to load company logo: ${JSON.stringify(err)}`);
                return null;
            }
        }),
    );

    const logoWidth = Math.round(HEADING_LOGO_WIDTH_PX * res);
    const logoTextGap = Math.round(LOGO_COMPANY_NAME_GAP_PX * res);
    const radius = Math.round(5 * res);
    ctx.font = `bold ${headingFontSize}px ${font}`;

    // Measure per-company segments: [logo?][gap?][title]
    type Segment = {
        logoImg: HTMLImageElement | null;
        logoW: number;
        logoH: number;
        gap: number;
        titleLine: string;
        titleW: number;
        totalW: number;
    };

    const maxTitleWidth = companies.length > 1
        ? canvasWidth * HEADING_TITLE_MAX_WIDTH_RATIO / companies.length
        : canvasWidth * HEADING_TITLE_MAX_WIDTH_RATIO;

    const segments: Segment[] = companies.map((c, i) => {
        const logoImg = logoImages[i];
        const logoW = logoImg ? logoWidth : 0;
        const logoH = logoImg ? (logoImg.height / logoImg.width) * logoW : 0;
        const gap = logoImg ? logoTextGap : 0;
        const titleLine = truncateText(ctx, c.companyName, maxTitleWidth);
        const titleW = ctx.measureText(titleLine).width;
        return { logoImg, logoW, logoH, gap, titleLine, titleW, totalW: logoW + gap + titleW };
    });

    // Measure "vs." text
    const vsGap = Math.round(VS_GAP_PX * res);
    const vsText = 'vs.';
    const vsWidth = ctx.measureText(vsText).width;
    const vsSectionWidth = segments.length > 1 ? vsGap + vsWidth + vsGap : 0;

    // Total row 1 width
    const segmentsWidth = segments.reduce((sum, s) => sum + s.totalW, 0);
    const totalRow1Width = segmentsWidth + vsSectionWidth * (segments.length - 1);

    // Row 1 height = tallest of all logos and title text
    const row1Height = Math.max(headingFontSize, ...segments.map((s) => s.logoH));

    // Total block: row 1 + gap + subheading
    const subheadingGap = subheadingHeight > 0 ? Math.round(NAME_SUBHEADING_GAP_PX * res) : 0;
    const totalBlockHeight = row1Height + subheadingGap + subheadingHeight;

    // Vertically center the block within the heading area, with padding top
    const blockStartY =
        Math.round((HEADING_HEIGHT_PX * res - totalBlockHeight) / 2) + Math.round(HEADING_PADDING_TOP_PX * res);

    // Draw row 1 centered horizontally
    let cursorX = Math.round((canvasWidth - totalRow1Width) / 2);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.font = `bold ${headingFontSize}px ${font}`;

    segments.forEach((seg, i) => {
        // "vs." separator before each segment after the first
        if (i > 0) {
            ctx.fillStyle = '#666666';
            const vsY = blockStartY + Math.round((row1Height - headingFontSize) / 2);
            ctx.fillText(vsText, cursorX + vsGap, vsY);
            cursorX += vsSectionWidth;
            ctx.fillStyle = '#000000';
        }

        // Logo
        if (seg.logoImg) {
            const logoX = cursorX;
            const logoY = blockStartY + Math.round((row1Height - seg.logoH) / 2);

            ctx.save();
            roundedRectPath(ctx, logoX, logoY, seg.logoW, seg.logoH, radius);
            ctx.clip();
            ctx.drawImage(seg.logoImg, logoX, logoY, seg.logoW, seg.logoH);
            ctx.restore();

            cursorX += seg.logoW + seg.gap;
        }

        // Title
        const titleY = blockStartY + Math.round((row1Height - headingFontSize) / 2);
        ctx.fillText(seg.titleLine, cursorX, titleY);
        cursorX += seg.titleW;
    });

    // Subheading centered below row 1
    if (subheadingLines.length > 0) {
        const subStartY = blockStartY + row1Height + subheadingGap;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.font = `400 ${subheadingFontSize}px ${font}`;
        ctx.fillStyle = '#000000';
        subheadingLines.forEach((line, i) => {
            ctx.fillText(line, canvasWidth / 2, subStartY + i * subheadingLineHeight);
        });
    }
}

type DrawLegendParams = {
    ctx: CanvasRenderingContext2D;
    legendItems: LegendItem[];
    config: DownloadConfig;
    canvasWidth: number;
    chartYEnd: number;
};

function drawLegend({ ctx, legendItems, config, canvasWidth, chartYEnd }: DrawLegendParams): number {
    if (!legendItems.length) {
        return 0;
    }

    const res = config.resolution;
    const indicatorSize = LEGEND_INDICATOR_SIZE_PX * res;
    const fontSize = LEGEND_FONT_SIZE_PX * res;
    const gap = LEGEND_INDICATOR_TEXT_GAP_PX * res;
    const columnGap = LEGEND_COLUMN_GAP_PX * res;
    const rowHeight = LEGEND_ROW_HEIGHT_PX * res;
    const paddingX = LEGEND_PADDING_X_PX * res;
    const startY = chartYEnd + LEGEND_TOP_MARGIN_PX * res;
    const numColumns = Math.ceil(legendItems.length / ITEMS_PER_COLUMN);
    const availableWidth = canvasWidth - paddingX * 2;
    const columnWidth = (availableWidth - columnGap * (numColumns - 1)) / numColumns;
    const maxTextWidth = columnWidth - indicatorSize - gap - 10 * res;

    ctx.font = `${fontSize}px ${FONT_FAMILY_CHART}`;
    ctx.textBaseline = 'middle';

    legendItems.forEach((item, index) => {
        const col = Math.floor(index / ITEMS_PER_COLUMN);
        const row = index % ITEMS_PER_COLUMN;
        const x = paddingX + col * (columnWidth + columnGap);
        const y = startY + row * rowHeight;

        ctx.fillStyle = item.color;
        ctx.beginPath();
        ctx.arc(x + indicatorSize / 2, y + indicatorSize / 2, indicatorSize / 2, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = '#333333';
        ctx.textAlign = 'left';

        const truncated = truncateText(ctx, getFieldLabel(item.label, item.totalChange), maxTextWidth);
        ctx.fillText(truncated, x + indicatorSize + gap, y + indicatorSize / 2);
    });

    return calculateLegendHeight(legendItems.length, res);
}

let cachedLogo: HTMLImageElement | null = null;

async function loadLogoImage(src: string): Promise<HTMLImageElement> {
    if (cachedLogo) {
        return cachedLogo;
    }
    cachedLogo = await loadImage(src);
    return cachedLogo;
}

function drawLogoFromImage(
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    config: DownloadConfig,
    canvasWidth: number,
    canvasHeight: number,
): void {
    const logoWidth = Math.round(MAX_LOGO_WIDTH_PX * config.resolution);
    const logoHeight = (img.height / img.width) * logoWidth;
    const padding = Math.round(LOGO_PADDING_PX * config.resolution);
    ctx.drawImage(img, canvasWidth - logoWidth - padding, canvasHeight - logoHeight - padding, logoWidth, logoHeight);
}

async function drawLogo(
    ctx: CanvasRenderingContext2D,
    logoSrc: string,
    config: DownloadConfig,
    canvasWidth: number,
    canvasHeight: number,
): Promise<void> {
    try {
        const img = await loadLogoImage(logoSrc);
        drawLogoFromImage(ctx, img, config, canvasWidth, canvasHeight);
    } catch (err) {
        Alert.warn(`Failed to draw logo: ${JSON.stringify(err)}`);
    }
}

/**
 * Pre-loads the logo image and returns the vertical space it needs (scaled).
 * Used by the component to compute how much height to reserve before rendering.
 */
export async function getLogoReserveHeight(logoSrc: string, resolution: number): Promise<number> {
    try {
        const img = await loadLogoImage(logoSrc);
        const logoWidth = Math.round(MAX_LOGO_WIDTH_PX * resolution);
        const logoHeight = (img.height / img.width) * logoWidth;
        const padding = Math.round(LOGO_PADDING_PX * resolution);
        return Math.round(logoHeight + padding);
    } catch (err) {
        Alert.warn(`Failed to get logo reserve height: ${JSON.stringify(err)}`);
        return 0;
    }
}

// ─── ECharts off-screen render ────────────────────────────────────────────────

type RenderEChartsToImageParams = {
    option: EChartsOption;
    logicalWidth: number;
    logicalHeight: number;
    config: DownloadConfig;
};

export function renderEChartsToImage({
    option,
    logicalWidth,
    logicalHeight,
    config,
}: RenderEChartsToImageParams): Promise<string> {
    return new Promise((resolve, reject) => {
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        container.style.top = '0';
        container.style.width = `${logicalWidth}px`;
        container.style.height = `${logicalHeight}px`;
        document.body.appendChild(container);

        const chart = echarts.init(container, undefined, {
            width: logicalWidth,
            height: logicalHeight,
            renderer: 'canvas',
        });

        const cleanup = () => {
            chart.dispose();
            if (document.body.contains(container)) {
                document.body.removeChild(container);
            }
        };

        chart.on('finished', () => {
            try {
                // ECharts supports png/jpeg; for webp we get PNG and let the compositor convert
                const type = config.exportAs === 'webp' ? 'png' : (config.exportAs as 'png' | 'jpeg');
                const dataUrl = chart.getDataURL({
                    type,
                    pixelRatio: config.resolution,
                    backgroundColor: config.backgroundColor,
                });
                cleanup();
                resolve(dataUrl);
            } catch (err) {
                cleanup();
                reject(err);
            }
        });

        chart.setOption({ ...option, animation: false });
    });
}

// ─── Image compositing ────────────────────────────────────────────────────────

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
        img.src = src;
    });
}

type CompositeChartImageParams = {
    chartImgUri: string;
    config: DownloadConfig;
    companies: CompanyHeadingInfo[];
    heading: string;
    isEmployee: boolean;
    logoSrc: string;
    scaledWidth: number;
    scaledChartHeight: number;
    legendItems: LegendItem[];
    logoReserveHeight: number;
};

export async function compositeChartImage({
    chartImgUri,
    config,
    companies,
    heading,
    isEmployee,
    logoSrc,
    scaledWidth,
    scaledChartHeight,
    legendItems,
    logoReserveHeight,
}: CompositeChartImageParams): Promise<string> {
    const chartImg = await loadImage(chartImgUri);

    const headingSpace = config.showHeadings ? Math.round(HEADING_HEIGHT_PX * config.resolution) : 0;
    const legendHeight = calculateLegendHeight(legendItems.length, config.resolution);

    const canvas = document.createElement('canvas');
    canvas.width = scaledWidth;
    canvas.height = scaledChartHeight + headingSpace + legendHeight + logoReserveHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Failed to get 2D canvas context');
    }

    // Background
    ctx.fillStyle = config.backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Headings
    await drawHeadings({ ctx, config, companies, heading, isEmployee, canvasWidth: canvas.width });

    // Chart image (stretch to fill the allocated chart area)
    ctx.drawImage(chartImg, 0, headingSpace, scaledWidth, scaledChartHeight);

    // Legend
    if (legendItems.length > 0) {
        drawLegend({
            ctx,
            legendItems,
            config,
            canvasWidth: canvas.width,
            chartYEnd: headingSpace + scaledChartHeight,
        });
    }

    // Logo
    await drawLogo(ctx, logoSrc, config, canvas.width, canvas.height);

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(URL.createObjectURL(blob));
            } else {
                reject(new Error('Failed to create image blob'));
            }
        }, `image/${config.exportAs}`);
    });
}
